import React, { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import FormField from "../ui/FormField";
import { getSunbeds, SunbedItem } from "../../services/sunbeds";
import { useToast } from "../../hooks/useToast";

interface EditCustomerPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (minutes: number, sunbedId: number) => Promise<void>;
  currentMinutes: number;
  currentSunbedId?: number;
}

export const EditCustomerPackageModal: React.FC<EditCustomerPackageModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  currentMinutes,
  currentSunbedId,
}) => {
  const toast = useToast();
  const [minutes, setMinutes] = useState<string>("");
  const [selectedSunbedId, setSelectedSunbedId] = useState<string>("");
  const [sunbedPackages, setSunbedPackages] = useState<SunbedItem[]>([]);
  const [loadingPackages, setLoadingPackages] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [packageError, setPackageError] = useState<string | null>(null);

  // Fetch sunbed packages
  useEffect(() => {
    const fetchPackages = async (): Promise<void> => {
      if (isOpen) {
        try {
          setLoadingPackages(true);
          const response = await getSunbeds();
          const packages = response.data || [];
          setSunbedPackages(packages);
        } catch (err) {
          console.error("Failed to fetch sunbed packages:", err);
          toast.error("Failed to load sunbed packages. Please try again.");
        } finally {
          setLoadingPackages(false);
        }
      }
    };
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Set initial values when modal opens or when currentSunbedId changes
  useEffect(() => {
    if (isOpen) {
      setMinutes(currentMinutes.toString());
      setError(null);
      // Set selected package - will be set after packages load if needed
      if (currentSunbedId) {
        setSelectedSunbedId(currentSunbedId.toString());
      } else {
        setSelectedSunbedId("");
      }
    }
  }, [isOpen, currentMinutes, currentSunbedId]);

  // Ensure selected package is set after packages are loaded
  useEffect(() => {
    if (isOpen && sunbedPackages.length > 0 && currentSunbedId) {
      // Verify the package exists in the list before setting
      const packageExists = sunbedPackages.some(pkg => pkg.id === currentSunbedId);
      if (packageExists) {
        setSelectedSunbedId(currentSunbedId.toString());
      }
    }
  }, [isOpen, sunbedPackages, currentSunbedId]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Reset errors
    setError(null);
    setPackageError(null);
    
    // Validate sunbed package selection
    if (!selectedSunbedId) {
      setPackageError("Please select a sunbed package.");
      return;
    }
    
    const val = parseInt(minutes, 10);
    if (isNaN(val)) {
      setError("Please enter a valid number of minutes.");
      return;
    }
    if (val < 0) {
      setError("Remaining minutes cannot be negative.");
      return;
    }
    if (val > currentMinutes) {
      setError(
        `New remaining minutes (${val}) cannot be greater than the current balance (${currentMinutes}).`
      );
      return;
    }

    const sunbedId = parseInt(selectedSunbedId, 10);
    if (isNaN(sunbedId)) {
      setPackageError("Invalid sunbed package selected.");
      return;
    }

    try {
      setIsSubmitting(true);
      await onUpdate(val, sunbedId);
      // Close modal after successful update
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error("Error updating package:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-[#885ABB]" />
              <p className="text-sm font-medium text-gray-700">Updating package...</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 border-b border-[#885ABB] flex justify-between items-center bg-[#885ABB] rounded-t-xl">
          <h2 className="text-xl font-bold text-white">
             Edit Customer Package
          </h2>
          <button 
            onClick={onClose}
            disabled={isSubmitting}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <FormField
              label="Sunbed Package: *"
              name="sunbedPackage"
              type="select"
              value={selectedSunbedId}
              onChange={(_, val) => {
                setSelectedSunbedId(val);
                setPackageError(null); // Clear error when user selects
              }}
              options={sunbedPackages.map((pkg) => ({
                value: pkg.id.toString(),
                label: pkg.label,
              }))}
              placeholder={loadingPackages ? "Loading packages..." : "Select a sunbed package"}
              className="w-full"
              disabled={loadingPackages}
              error={packageError || undefined}
            />
            {packageError && (
              <p className="text-xs text-red-600 mt-1">
                {packageError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <FormField
              label="Remaining Minutes:"
              name="minutes"
              type="number"
              value={minutes}
              onChange={(_, val) => setMinutes(val)}
              className="w-full" // FormField usually handles full width but just in case
            />
            {error && (
              <p className="text-xs text-red-600 mt-1">
                {error}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              <span className="font-bold text-gray-700">Note:</span> The remaining minutes can only be decreased. Leave the field untouched to keep the current value.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
             <button
               type="button"
               onClick={onClose}
               disabled={isSubmitting}
               className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
             >
               Cancel
             </button>
             <button
               type="submit"
               disabled={isSubmitting || loadingPackages}
               className="px-6 py-2.5 rounded-lg bg-[#885ABB] hover:bg-[#6f47a2] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
             >
               {isSubmitting && <Loader2 size={16} className="animate-spin" />}
               {isSubmitting ? "Updating..." : "Update"}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
